import { FunctionComponent, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import styles from '../../Screens/Analytics/Analytics.module.css';
import { kpiLeadsDrilldownData, kpiTaskDrilldownData, } from '../../Services/analytics';
import { themeColors } from "../../Components/theme";


/**
 * CountBox Props
 * ---------------------------------------
 * count: Numeric KPI value displayed
 * text: Label text (e.g., "Total Leads")
 * style: Inline styling object for dynamic colors
 * role: User role from Redux
 * filterText: Name of filter-type (Pending, Completed, etc.)
 * history: React Router history, used for navigation
 * filter: Current filter (MTD, YTD, PM, All, CUSTOM, T, Y)
 * contacts: All contacts data from Redux
 * user: Auth user object
 * organizationUsers, teamLeadUsers, branchUsers: User trees
 * taskFilter, leadFilter: Applied filter objects
 * source: Boolean flag for analytics type
 * theme: Light or dark mode
 * selectOrgId: Organization ID fallback
 */
type props = {
  count: string | number;
  text: string;
  style: any;
  role: any;
  filterText?: string;
  history?: any;
  filter: 'MTD' | 'YTD' | 'PM' | 'All' | 'CUSTOM' | 'T' | 'Y';
  contacts: any;
  user: any;
  organizationUsers: any[];
  teamLeadUsers: any[];
  branchUsers: any;
  taskFilter?: any;
  leadFilter?: any;
  source: boolean;
  theme: any;
  selectOrgId: any;
};

const CountBox: FunctionComponent<props> = ({
  count,
  text,
  style,
  role,
  filterText,
  history,
  filter,
  contacts,
  user,
  organizationUsers,
  teamLeadUsers,
  branchUsers,
  taskFilter,
  leadFilter,
  source,
  theme,
  selectOrgId
}) => {
  const [allContacts, setAllContacts] = useState<any[] | undefined>(undefined);
  /**
   * Extract all contacts into a single flattened array.
   * Contacts are stored by grouped keys in Redux.
   */
  useEffect(() => {
    let contact: any[] = [];
    Object.keys(contacts.data).forEach((key) => {
      contact = [...contact, ...contacts.data[key]];
    });
    setAllContacts(contact);
  }, [contacts.data]);

  /**
 * Handles drilldown for non-Total Leads boxes.
 * Navigates to relevant drilldown page based on filterText.
 */
  const setFilter = async () => {
    let mapReportingTo: { [key: string]: string } = {};
    if (organizationUsers) {
      organizationUsers.forEach((item: any) => {
        if (mapReportingTo[item.uid] === undefined) {
          mapReportingTo[item.uid] = item.reporting_to;
        }
      });
    }

    // TASK-BASED DRILLDOWN
    if (filterText === 'Pending' || filterText === 'Completed') {

      const drillDownData = await kpiTaskDrilldownData(
        filter,
        user.uid || user?.auth_id || selectOrgId,
        filterText,
        taskFilter,
        leadFilter,
        true,
        source
      );

      const drillDownDataLocalStore = {
        ...drillDownData,
        source
      }

      localStorage.setItem(
        'taskDrilldownData',
        JSON.stringify(drillDownDataLocalStore)
      );

      // 🔹 MAIN NAVIGATION (FORCE RELOAD)
      history.push('/taskDrilldownData', {
        drilldownData: drillDownDataLocalStore,
        ts: Date.now(), // 👈 VERY IMPORTANT
      });

    }
    else {

      // LEADS DRILLDOWN
      const drillDownData = await kpiLeadsDrilldownData(
        user.uid || user?.auth_id || selectOrgId,
        filter,
        filterText,
        taskFilter,
        leadFilter,
        true
      );

      const drillDownDataLocalStore = {
        ...drillDownData,
        organizationid: user.organization_id || selectOrgId,
        source
      }

      localStorage.setItem(
        'drilldownData',
        JSON.stringify(drillDownDataLocalStore)
      );

      // 🔹 MAIN NAVIGATION (FORCE RELOAD)
      history.push('/drilldownData', {
        drilldownData: drillDownDataLocalStore,
        ts: Date.now(), // 👈 VERY IMPORTANT
      });

    }
  };

  /**
   * Handles Total Leads drilldown logic.
   * Uses different format for drilldownData stored in localStorage.
   */
  const setTotalLeadFilter = async () => {
    let mapReportingTo: { [key: string]: string } = {};
    if (organizationUsers) {
      organizationUsers.forEach((item: any) => {
        if (mapReportingTo[item.uid] === undefined) {
          mapReportingTo[item.uid] = item.reporting_to;
        }
      });
    }

    // TASK-BASED DRILLDOWN
    if (filterText === 'Pending' || filterText === 'Completed') {
      const drillDownData = await kpiTaskDrilldownData(
        filter,
        user.uid || user?.auth_id || selectOrgId,
        filterText,
        taskFilter,
        leadFilter,
        true,
        source
      );
      const drillDownDataLocalStore = {
        ...drillDownData,
        source
      }

      localStorage.setItem(
        'taskDrilldownData',
        JSON.stringify(drillDownDataLocalStore)
      );

      // 🔹 MAIN NAVIGATION (FORCE RELOAD)
      history.push('/taskDrilldownData', {
        drilldownData: drillDownDataLocalStore,
        ts: Date.now(), // 👈 VERY IMPORTANT
      });

    }
    else {

      // TOTAL LEADS DRILLDOWN (Different payload format)
      const totalLeadReq = {
        uid: user.uid || user?.auth_id || selectOrgId,
        leadFilter: leadFilter,
        taskFilter: taskFilter,
        role: true,
        organizationid: user.organization_id || selectOrgId,
        source: false
      }


      localStorage.setItem(
        'drilldownData',
        JSON.stringify(totalLeadReq)
      );

      // 🔹 MAIN NAVIGATION (FORCE RELOAD)
      history.push('/drilldownData', {
        drilldownData: totalLeadReq,
        ts: Date.now(), // 👈 VERY IMPORTANT
      });
    }
  };
  /** Theme-based box styles */
  const countBoxStyles = { backgroundColor: theme ? themeColors.cardBackgroundColor : themeColors.cardBackgroundColorDark, color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark }
  const countStyles = { color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark }

  return (
    <>
      {text === "Total Leads" ? <>
        <div className={styles.countBox1}
          style={{ borderLeft: `4px solid ${style.color}`, ...countBoxStyles, cursor: 'pointer' }}
          onClick={setTotalLeadFilter}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',

            }}
          >
            <p className={styles.leadText} style={style}>
              {text}
            </p>
          </div>
          <p className={styles.leadCount} style={countStyles}>{count}</p>
        </div>
      </> : <>
        <div className={styles.countBox}
          style={{ borderLeft: `4px solid ${style.color}`, ...countBoxStyles }}
          onClick={setFilter}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',

            }}
          >
            <p className={styles.leadText} style={style}>
              {text}
            </p>
          </div>
          <p className={styles.leadCount} style={countStyles}>{count}</p>
        </div>
      </>
      }
    </>
  );
};

/**
 * Maps Redux state to component props.
 */
const mapStateToProps = (state: any) => {
  return {
    role: state.user.role,
    contacts: state.contacts,
    user: state.user.data,
    organizationUsers: state.organizationUsers.data,
    teamLeadUsers: state.teamLeadUsers.data,
    branchUsers: state.branchUsers.data,
    theme: state.theme.isLightMode,
    selectOrgId: state.selectOrganizationId.data
  };
};

export default connect(mapStateToProps)(CountBox);
